import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { theme } from './theme/theme';
import Navbar from './components/navbar/navbar';
import BackgroundScene from './components/3d/BackgroundScene';
import Home from './pages/Home';
import Projects from './pages/Projects';
import Blogs from './pages/Blogs';
import ArticleDetail from './pages/ArticleDetail';
import SocialLinks from './pages/SocialLinks';
import Admin from './pages/Admin';

/* ─── Animated route wrapper ─── */
const pageTransition = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div {...pageTransition}>
              <Home />
            </motion.div>
          }
        />
        <Route
          path="/projects"
          element={
            <motion.div {...pageTransition}>
              <Projects />
            </motion.div>
          }
        />
        <Route
          path="/blogs"
          element={
            <motion.div {...pageTransition}>
              <Blogs />
            </motion.div>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <motion.div {...pageTransition}>
              <ArticleDetail />
            </motion.div>
          }
        />
        <Route
          path="/links"
          element={
            <motion.div {...pageTransition}>
              <SocialLinks />
            </motion.div>
          }
        />
        <Route
          path="/admin"
          element={
            <motion.div {...pageTransition}>
              <Admin />
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

/* ─── App root ─── */
const App: React.FC = () => {
  React.useEffect(() => {
    document.title = 'about @shvms';
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: 'background.default',
            color: 'text.primary',
            position: 'relative',
          }}
        >
          {/* Persistent 3D background behind all pages */}
          <BackgroundScene />

          {/* Global navbar */}
          <Navbar />

          {/* Routed page content */}
          <AnimatedRoutes />
        </Box>
      </Router>
    </ThemeProvider>
  );
};

export default App;